import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/kalachevo";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const kalachevo: CaseStudy = {
  slug: "kalachevo",
  title: "Экоферма Калачево",
  cardTitle: "Разработка сайта экофермы Калачево с интернет-магазином",
  subtitle: "Семейная экоферма с сыроварней и пекарней",
  industry: { label: "Еда и продукты", slug: "food" },
  eyebrow: "Интернет-магазин · Фермерские продукты",
  h1: "Разработка сайта экофермы Калачево с интернет-магазином",
  lead:
    "Сделали сайт семейной экофермы Калачево: единый рассказ о ферме, сыроварне и пекарне, интернет-магазин с каталогом и корзиной, варианты доставки и самовывоза и раздел для прессы и партнёров.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт производителя продуктов" },

  seo: {
    primaryKeyword: "разработка сайта экофермы с интернет-магазином",
    secondaryKeywords: [
      "сайт фермерского хозяйства",
      "интернет-магазин фермерских продуктов",
      "сайт экофермы разработка",
      "e-commerce для локального производителя продуктов",
    ],
    semanticEntities: [
      "экоферма",
      "сыроварня",
      "пекарня",
      "каталог фермерских продуктов",
      "доставка по Москве и области",
      "самовывоз",
      "автолавка",
      "checkout",
      "пресса и партнёры",
    ],
    excludedKeywords: [
      "фермерские продукты купить",
      "сыр купить",
      "молоко доставка",
      "экоферма Домодедово",
    ],
    title: "Сайт экофермы Калачево — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт семейной экофермы с сыроварней, пекарней, интернет-магазином, доставкой и офлайн-точками.",
    h1: "Разработка сайта экофермы Калачево с интернет-магазином",
    slug: "kalachevo",
    canonicalUrl: `${SITE_URL}/cases/kalachevo`,
    ogTitle: "Сайт экофермы Калачево с интернет-магазином",
    ogDescription:
      "Ферма, сыроварня, пекарня, каталог продуктов, корзина, доставка и самовывоз на одном сайте.",
    ogImage: `${SITE_URL}${dir}/kalachevo-og.webp`,
    robotsIndex: true,
    datePublished: "2023-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и оформление заказа",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога фермерских продуктов",
    },
    relatedCases: [
      {
        label: "Разработка интернет-магазина цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и корзина",
      },
      {
        label: "E-commerce платформа для производителя косметики Levrana",
        to: "/cases/levrana",
        note: "Каталог и оформление заказа для производителя",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт производителя продуктов с интернет-магазином?",
    text: "Соберём каталог, корзину и варианты доставки и самовывоза для вашего производства.",
    button: "Обсудить сайт производителя продуктов",
  },

  client: {
    name: "Экоферма Калачево",
    url: "https://kalachevo.com/",
    city: "Московская область",
    industry: "Фермерские продукты",
  },
  year: "С 1996 года",
  services: [
    "Дизайн сайта экофермы",
    "Разработка интернет-магазина",
    "Каталог, карточка товара и корзина",
    "Разделы доставки, самовывоза и офлайн-точек",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "Экоферма Калачево" },
    { label: "Тип проекта", value: "Сайт экофермы с интернет-магазином" },
    { label: "Платформа", value: "1С-Битрикс" },
    { label: "Год основания фермы", value: "1996" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Сайт, объединяющий экоферму, сыроварню, пекарню, прессу и интернет-магазин",
      "Каталог и карточки фермерских продуктов с корзиной",
      "Checkout с адресом, скидочным кодом, способом получения и оплатой",
      "Разделы магазинов, автолавки, доставки по Москве и области и самовывоза",
      "Раздел для прессы и партнёров",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Экоферма Калачево работает с 1996 года и объединяет ферму, сыроварню и пекарню. Нужен был сайт, который рассказывает о происхождении продукта и одновременно продаёт его через интернет-магазин.",
        "Вторая часть задачи — показать реальные варианты получения товара: магазины, автолавку, доставку по Москве и области и самовывоз, — и оформить checkout под них.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тёплая natural-подача фермы",
    paragraphs: [
      "Интерфейс собран на тёплом natural/cream фоне с реальными продуктами и фермой — без стоковых полей и клише агробизнеса.",
      "История фермы, сыроварни и пекарни подана как один сквозной storytelling-блок перед каталогом.",
    ],
    bullets: [
      "Тёплая natural/cream палитра",
      "Реальные продукты и ферма вместо стоковых кадров",
      "Единый рассказ о ферме, сыроварне и пекарне",
    ],
  },

  identity: {
    fonts: [
      { name: "Orchidea Pro", role: "Заголовки", sample: "Экоферма Калачево" },
      { name: "Circe Rounded", role: "Текст и интерфейс", sample: "Каталог продуктов" },
    ],
    colors: [
      { hex: "#F3ECDE", name: "Фон витрины" },
      { hex: "#6B7A4F", name: "Акцент бренда" },
      { hex: "#2B2620", name: "Текст" },
    ],
  },

  cover: img(
    "kalachevo-case-cover",
    "Главная страница сайта экофермы Калачево с интернет-магазином",
    6,
  ),

  gallery: [
    img("kalachevo-farm", "Раздел о ферме, сыроварне и пекарне Калачево", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: история фермы, каталог продуктов и оформление заказа.",
      "Состояния корзины и checkout в мобильной версии показаны заполненными.",
    ],
    images: [
      mobile("kalachevo-mobile-home", "Мобильная главная сайта экофермы Калачево"),
      mobile("kalachevo-mobile-shop", "Мобильный интернет-магазин экофермы Калачево"),
    ],
  },

  columnBlocks: [
    {
      id: "origin",
      title: "Происхождение продукта",
      paragraphs: [
        "Ферма, сыроварня и пекарня объединены в один рассказ о происхождении продукта — от фермы до конкретного товара в каталоге.",
      ],
      bullets: ["Экоферма", "Сыроварня", "Пекарня"],
    },
    {
      id: "shop",
      title: "Интернет-магазин",
      paragraphs: [
        "Каталог, карточки товаров и корзина показывают заполненные состояния, а checkout собирает адрес, скидочный код, способ получения и оплату.",
      ],
      bullets: [
        "Каталог фермерских продуктов",
        "Карточка товара и корзина",
        "Checkout со скидочным кодом и способом получения",
      ],
    },
    {
      id: "delivery",
      title: "Доставка и самовывоз",
      paragraphs: [
        "Раздел получения товара показывает реальные варианты: магазины и автолавку, доставку по Москве и области и самовывоз.",
      ],
      columns: [
        { title: "Офлайн", items: ["Магазины", "Автолавка"] },
        { title: "Онлайн", items: ["Доставка по Москве и области", "Самовывоз"] },
      ],
    },
    {
      id: "press",
      title: "B2B и пресса",
      paragraphs: [
        "Короткий раздел для прессы и партнёров дополняет позиционирование фермы как узнаваемого локального производителя.",
      ],
      bullets: ["Раздел для прессы", "Информация для партнёров"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка интернет-магазина", to: routes.ecom },
    ],
  },
};
